import "@/App.css";
import { useEffect, useState } from "react";
import { loadBranding } from "@/lib/branding";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider, useTheme, THEMES } from "@/context/ThemeContext";
import ThermalBackground from "@/components/ThermalBackground";
import NoiseOverlay from "@/components/NoiseOverlay";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";
import { ConfirmDialogProvider } from "@/hooks/useConfirm";
import Login from "@/pages/Login";
import LoginPageSteeltooth from "@/steeltooth-login/LoginPage";

/**
 * Picks the sign-in screen from Admin → Settings → Branding ("Landing page"
 * selector): "elite" = the original portal login, "steeltooth" = the ported
 * Steeltooth login. Branding is cached by lib/branding after one fetch.
 */
function LoginGate() {
  // null = branding still loading. Never render one login page then swap —
  // that flashed Elite before Steeltooth. Dark splash until the choice is
  // known; on any fetch failure the default is the Elite login.
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    loadBranding()
      .then((b) => setBranding(b || {}))
      .catch(() => setBranding({}));
  }, []);
  if (branding === null) {
    return <div className="min-h-screen bg-[#050505]" data-testid="login-loading" />;
  }
  if (branding.login_page === "steeltooth") return <LoginPageSteeltooth />;
  return (
    <div
      id="login-root"
      className="relative min-h-screen overflow-hidden bg-[#050505]"
      data-wg={branding.workgroup || "elite"}
      data-testid="login-root"
    >
      <ThermalBackground />
      <NoiseOverlay />
      <Login appName={branding.app_name} />
    </div>
  );
}
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import Legal from "@/pages/Legal";
import EstPublicQuotePage from "@/estimating/pages/PublicQuotePage";
import LeadsModPage from "@/pages/leads-mod/LeadsPage";


function AppRouter() {
  const location = useLocation();
  const { theme } = useTheme();

  // Apply custom text branding (browser window title) from Admin → Branding,
  // and remember the workgroup default theme: the sign-in screen is drawn in
  // it so each workgroup's look starts before login. Other public pages
  // (legal, shared quotes) stay dark; the user's own choice applies inside
  // the authenticated dashboard.
  const [loginTheme, setLoginTheme] = useState("dark");
  useEffect(() => {
    loadBranding()
      .then((b) => setLoginTheme(THEMES.includes(b?.default_theme) ? b.default_theme : "dark"))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const applied = location.pathname.startsWith("/dashboard") ? theme : location.pathname === "/" ? loginTheme : "dark";
    const root = document.documentElement;
    root.classList.toggle("light", applied === "light");
    root.classList.toggle("dark", applied !== "light");
    ["orange", "green", "blue", "graphic"].forEach((t) => root.classList.toggle(t, applied === t));
    root.dataset.theme = applied;
  }, [location.pathname, theme, loginTheme]);

  // Detect OAuth callback synchronously (fragment) BEFORE any route/auth check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<LoginGate />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/legal/:doc" element={<Legal />} />
      {/* Estimating module: recipient-facing quote page (no auth — the
          token IS the credential; share links mint /q/{token}). */}
      <Route path="/q/:token" element={<EstPublicQuotePage />} />
      {/* Leads module (contained port): own page behind auth. */}
      <Route
        path="/leads-mod"
        element={
          <ProtectedRoute>
            <LeadsModPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <ConfirmDialogProvider>
              <AppRouter />
              <InstallPrompt />
            </ConfirmDialogProvider>
          </ThemeProvider>
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#0a0a0a",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
